import { css } from '@emotion/react';
import {
  Box,
  Button,
  COLOR_PALETTE,
  Flex,
  Image,
  ImageCropper,
  VisuallyHidden,
  openAlertModal,
  rem,
} from '@inflearn/ds-react-next';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { logger } from '../../../../../../../resources/Logger';
import {
  ALLOWED_IMAGE_FILE_EXTENSIONS,
  type ProfileValidatorResult,
  useProfileImageValidator,
} from '../../../../../../hooks/useProfileImageValidator';
import useClientAccountProfileImageUploadMutation from '../../../../../../mutations/useClientAccountProfileImageUploadMutation';
import type { ValidationStatus } from '../../../../../../utils/validation';
import ValidationMessage from '../../../../../common/ValidationMessage';
import type { ProfileEditForm } from './Edit';

const PROFILE_SIZE = 100;
const ID_FILE_INPUT = 'profile-file';
export const DEFAULT_IMAGE_URL = 'https://cdn.inflearn.com/public/main/profile/default_profile.png';

export const ProfileImage = () => {
  const { t } = useTranslation();
  const form = useFormContext<ProfileEditForm>();
  const imageId = form.getValues('profileImageFileId');
  const url = form.getValues('profileImageUrl');

  const [validatorResult, setValidatorResult] = useState<ProfileValidatorResult | null>(null);

  const handleChangeValidatorResult = (result: ProfileValidatorResult | null) => {
    setValidatorResult(result);
  };

  const extensionStatus = (): ValidationStatus => {
    if (validatorResult === 'INVALID_EXTENSION') {
      return 'error';
    }

    if (validatorResult === 'INVALID_SIZE') {
      return 'success'; // 이미지 크기 유효성을 통과하지 못한 경우, 사이즈는 통과되는 것으로 표시
    }

    return isNil(imageId) ? 'help' : 'success';
  };

  const sizeStatus = (): ValidationStatus => {
    if (validatorResult === 'INVALID_EXTENSION') {
      return 'help';
    }

    if (validatorResult === 'INVALID_SIZE') {
      return 'error';
    }

    return isNil(imageId) ? 'help' : 'success';
  };

  return (
    <Flex gap={16}>
      <ProfileImagePreview url={url} />
      <Flex direction="column" justify="space-between">
        <UploadButton onChangeValidator={handleChangeValidatorResult} />
        <Flex direction="column" gap={4}>
          <Flex gap={8}>
            <ValidationMessage status={extensionStatus()} message={t('png, jpg, jpeg의 확장자')} />
          </Flex>
          <Flex gap={8}>
            <ValidationMessage status={sizeStatus()} message={t('1MB 이하의 이미지')} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const ProfileImagePreview = ({ url }: { url: string }) => (
  <Box component="picture" w={PROFILE_SIZE} h={PROFILE_SIZE} css={styleProfileImage}>
    <Image src={url || DEFAULT_IMAGE_URL} />
  </Box>
);

const UploadButton = ({
  onChangeValidator,
}: {
  onChangeValidator: (result: ProfileValidatorResult | null) => void;
}) => {
  const { t } = useTranslation();
  const { validateImageFile } = useProfileImageValidator();
  const form = useFormContext<ProfileEditForm>();
  const [isOpened, setIsOpened] = useState(false);
  const [fileName, setFileName] = useState('');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const { mutate: uploadMutate, isLoading } = useClientAccountProfileImageUploadMutation();

  const handleCropImage = (file: File) => {
    uploadMutate(
      { file },
      {
        onSuccess: (data) => {
          if (data.statusCode !== 'OK') {
            logger.error(JSON.stringify(data));

            openAlertModal({
              title: t('파일 업로드 실패'),
              children: <>{t('파일 업로드에 실패했습니다. 다시 시도해 주세요.')}</>,
              labels: {
                confirm: t('확인'),
              },
              footerType: 'right',
              confirmProps: {
                color: 'infgreen',
                type: 'submit',
                size: 'md',
              },
            });

            return;
          }

          form.setValue('profileImageFileId', data.data?.fileId || null, {
            shouldDirty: true,
          });
          form.setValue('profileImageUrl', data.data?.cdnUrl || '', {
            shouldDirty: true,
          });
          setIsOpened(false);
        },
        onError: (error) => {
          logger.error(error);
        },
      },
    );
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.addEventListener('load', () => {
        if (!validateImageFile({ file, onChange: onChangeValidator })) {
          return;
        }

        setIsOpened(true);

        if (e.target.files) {
          setFileName(file.name);
          setImageSrc(reader.result?.toString() || '');
        }
      });
      reader.readAsDataURL(file);

      e.target.value = '';
    }
  };

  const handleReset = () => {
    onChangeValidator(null);
    form.setValue('profileImageFileId', null, { shouldDirty: true });
    form.setValue('profileImageUrl', '', { shouldDirty: true });
  };

  return (
    <>
      <Flex gap={8}>
        <VisuallyHidden>
          <input
            id={ID_FILE_INPUT}
            type="file"
            onChange={onSelectFile}
            accept={ALLOWED_IMAGE_FILE_EXTENSIONS.join(',')}
          />
        </VisuallyHidden>
        <Button component="label" htmlFor={ID_FILE_INPUT} variant="default">
          {t('변경')}
        </Button>
        {!isNil(form.getValues('profileImageFileId')) && (
          <Button variant="default" onClick={handleReset}>
            {t('초기화')}
          </Button>
        )}
      </Flex>
      <ImageCropper
        defaultAspect={1}
        opened={isOpened}
        onConfirm={handleCropImage}
        fileName={fileName}
        src={imageSrc}
        onClose={() => setIsOpened(false)}
        loading={isLoading}
      />
    </>
  );
};

const styleProfileImage = css({
  borderRadius: '16px',
  border: `1px solid ${COLOR_PALETTE.gray[3]}`,
  background: COLOR_PALETTE.gray[0],
  overflow: 'hidden',
  img: { height: `${rem(PROFILE_SIZE)} !important` },
});
